import {
  faCircleNodes,
  faLinkSimple,
} from "@fortawesome/pro-regular-svg-icons";
import { Breadcrumbs, Button, Icon } from "@intility/bifrost-react";
import { type ReactNode, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import type { ClientListItem } from "../api/types";
import standaloneDark from "../assets/standalone/standalone-dark.svg";
import standaloneLight from "../assets/standalone/standalone-light.svg";
import { clientPath } from "../lib/clientPath";
import { isFromMyHub } from "../lib/fromMyHub";
import { ClientConnectModal } from "./ClientConnectModal";
import { HeaderIllustration } from "./platforms/PlatformHeaderIllustration";
import { useCanEditClient } from "./useCanEditClient";
import "./platforms/PlatformHeaderIllustration.css";

// The header of a standalone client's page, built like a platform's: the
// breadcrumbs, an icon and a title with a line under it, an illustration at the
// right, and below them whatever the page puts in a button row. There is no tab
// row.
function StandaloneHeader({
  crumb,
  children,
}: {
  /** The last breadcrumb: the client's name, or "New client". */
  crumb: string;
  /** The button row; none when there are no buttons. */
  children?: ReactNode;
}) {
  const fromMyHub = isFromMyHub(useLocation().state);
  return (
    <header
      style={{
        backgroundColor: "var(--bfc-base-2)",
        borderBottom: "1px solid var(--bfc-base-c-dimmed)",
      }}
    >
      <div className="bf-page-padding-x platform-header-top">
        <div className="bf-page-padding-y platform-header-text">
          <Breadcrumbs>
            <Breadcrumbs.Item>
              <Link to="/">Home</Link>
            </Breadcrumbs.Item>
            <Breadcrumbs.Item>
              {/* Opened from My Hub: the crumb leads back there, not to
                  Clients. */}
              {fromMyHub ? (
                <Link to="/myhub">My Hub</Link>
              ) : (
                <Link to="/clients">Clients</Link>
              )}
            </Breadcrumbs.Item>
            <Breadcrumbs.Item>{crumb}</Breadcrumbs.Item>
          </Breadcrumbs>
          <div
            style={{
              marginTop: 12,
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            {/* Where a platform has its logo: the icon of a client that is not
                connected to one. The icon, the title and the line under it are
                all base 2. */}
            <Icon
              icon={faLinkSimple}
              className="bfc-base-2"
              style={{ fontSize: 32 }}
              aria-label="Not connected to a platform"
            />
            <h1 className="bf-h1 bfc-base-2" style={{ margin: 0 }}>
              Standalone client
            </h1>
          </div>
          <p
            className="bf-p bfc-base-2"
            style={{ marginTop: 8, marginBottom: 0, maxWidth: "72ch" }}
          >
            Client is not connected to a platform
          </p>
        </div>
        <HeaderIllustration
          light={standaloneLight}
          dark={standaloneDark}
          width={258}
        />
      </div>
      {children}
    </header>
  );
}

// The header of a standalone client's page, with the button row: Connect to
// platform.
export function StandaloneClientHeader({ client }: { client: ClientListItem }) {
  const navigate = useNavigate();
  const location = useLocation();
  const canEdit = useCanEditClient(client);
  const [connecting, setConnecting] = useState(false);

  return (
    <StandaloneHeader crumb={client.name}>
      <div
        className="bf-page-padding-x"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 12,
          flexWrap: "wrap",
          paddingBlock: 12,
          borderTop: "1px solid var(--bfc-base-c-dimmed)",
        }}
      >
        <Button
          variant="filled"
          aria-label="Connect to platform"
          state={canEdit ? undefined : "inactive"}
          onClick={canEdit ? () => setConnecting(true) : undefined}
        >
          <Icon icon={faCircleNodes} />
          <span className="from-small" style={{ marginLeft: 8 }}>
            Connect to platform
          </span>
        </Button>
      </div>
      <ClientConnectModal
        client={client}
        open={connecting}
        onClose={() => setConnecting(false)}
        // The client is in a platform now, so it is viewed from there.
        onConnected={(platformId) =>
          navigate(clientPath({ id: client.id, platformId }), {
            replace: true,
            // Still opened from where it was (My Hub), for the breadcrumb.
            state: location.state,
          })
        }
      />
    </StandaloneHeader>
  );
}

// The same header for a client that is being created: no client yet, so no
// buttons.
export function NewStandaloneClientHeader() {
  return <StandaloneHeader crumb="New client" />;
}
